import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, PlayCircle, TrendingUp, Building2, CalendarClock } from "lucide-react";
import { scrollToId } from "@/components/Reveal";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1768916321598-7869ef2db8e0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGxpcXVpZCUyMGVtZXJhbGQlMjBmaW5hbmNpYWwlMjBmbG93JTIwM2R8ZW58MHx8fHwxNzkwODU0MzIyfDA&ixlib=rb-4.1.0&q=85";

const LINES = [
  { text: "Paranın her hareketi,", accent: false },
  { text: "tek akışta.", accent: true },
];

const MaskedLine = ({ children, delay, accent }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className={`block ${accent ? "text-gradient-emerald" : "text-slate-50"}`}
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const MetricPill = ({ testid, icon: Icon, label, value, sub, className, delay }) => (
  <motion.div
    data-testid={testid}
    initial={{ opacity: 0, y: 24, scale: 0.95 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
    className={`glass-panel absolute z-20 rounded-2xl border border-white/10 px-5 py-4 shadow-[0_18px_50px_rgba(0,0,0,0.5)] ${className}`}
  >
    <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.14em] text-slate-400">
      <Icon className="h-3.5 w-3.5 text-emerald-400" />
      {label}
    </div>
    <div className="font-mono-num mt-1.5 text-xl font-semibold text-slate-50">
      {value}
    </div>
    <div className="mt-0.5 text-xs font-medium text-emerald-400">{sub}</div>
  </motion.div>
);

const Hero = () => {
  const navigate = useNavigate();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.22]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="hero"
      data-testid="hero-section"
      ref={ref}
      className="relative flex min-h-screen items-center overflow-hidden"
    >
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <img
          src={HERO_IMAGE}
          alt=""
          className="h-full w-full object-cover opacity-45"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#07090E]/70 via-[#07090E]/55 to-[#07090E]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#07090E]/90 via-transparent to-[#07090E]/60" />
      </motion.div>

      <div className="pointer-events-none absolute -left-40 top-1/3 h-[480px] w-[480px] rounded-full bg-emerald-500/15 blur-[140px]" />
      <div className="pointer-events-none absolute right-0 top-0 h-[380px] w-[380px] rounded-full bg-emerald-400/10 blur-[120px]" />

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-28 pt-40 sm:px-6 lg:px-12"
      >
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="mb-8 inline-flex items-center gap-2.5 rounded-full border border-emerald-400/25 bg-emerald-400/5 px-4 py-1.5"
        >
          <span className="animate-pulse-dot h-2 w-2 rounded-full bg-emerald-400" />
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-400">
            Nakit akışı canlı
          </span>
        </motion.div>

        <h1
          data-testid="hero-headline"
          className="font-display max-w-3xl text-4xl font-extrabold leading-[1.06] tracking-tight sm:text-6xl lg:text-7xl"
        >
          {LINES.map((line, i) => (
            <MaskedLine key={line.text} delay={0.25 + i * 0.14} accent={line.accent}>
              {line.text}
            </MaskedLine>
          ))}
        </h1>

        <motion.p
          data-testid="hero-subheadline"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.65 }}
          className="mt-7 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg"
        >
          Akış; gelirlerini, giderlerini, banka ve cari hesaplarını, yaklaşan
          ödeme ve tahsilatlarını tek panelde toplar. Finansal raporların
          saniyeler içinde hazır — sen işine odaklan.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-primary-cta"
            onClick={() => scrollToId("#erken-erisim")}
            className="group inline-flex items-center gap-2 rounded-full bg-emerald-500 px-7 py-3.5 text-base font-semibold text-emerald-950 transition-all duration-300 hover:bg-emerald-400 hover:shadow-[0_0_40px_rgba(0,255,135,0.4)]"
          >
            Erken Erişim Al
            <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
          <button
            data-testid="hero-secondary-demo-cta"
            onClick={() => navigate("/giris", { state: { mode: "register" } })}
            className="group inline-flex items-center gap-2 rounded-full border border-white/15 px-7 py-3.5 text-base font-semibold text-slate-200 transition-all duration-300 hover:border-emerald-400/50 hover:text-emerald-300"
          >
            <PlayCircle className="h-5 w-5 text-emerald-400" />
            Ücretsiz Hesap Aç
          </button>
        </motion.div>
      </motion.div>

      <MetricPill
        testid="hero-metric-cashflow"
        icon={TrendingUp}
        label="Aktif Nakit Akışı"
        value="₺1.420.000"
        sub="+%12,4 bu ay"
        delay={1.05}
        className="right-[8%] top-[24%] hidden lg:block"
      />
      <MetricPill
        testid="hero-metric-balance"
        icon={Building2}
        label="Toplam Banka Bakiyesi"
        value="₺3.850.000"
        sub="4 bağlı hesap"
        delay={1.2}
        className="right-[22%] top-[46%] hidden xl:block"
      />
      <MetricPill
        testid="hero-metric-upcoming"
        icon={CalendarClock}
        label="Yaklaşan Tahsilat"
        value="₺480.000"
        sub="sonraki 14 gün"
        delay={1.35}
        className="bottom-[14%] right-[10%] hidden lg:block"
      />

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2"
      >
        <div className="flex h-10 w-6 items-start justify-center rounded-full border border-white/20 p-1.5">
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
            className="h-2 w-1 rounded-full bg-emerald-400"
          />
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
